import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import ProfileEditModal from './ProfileEditModal';
import './ProfilePage.css';

const ProfilePage = () => {
  const { t, language } = useLanguage();
  const { user, isLoading, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- HANDLER FUNCTIONS ---
  const handleChangePassword = () => {
    // Show a confirmation dialog to the user
    if (window.confirm(t('dashboard.profile.confirmChangePassword'))) {
      logout();
      navigate('/forgot-password');
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSaveProfile = async (formData) => {
    try {
      const response = await apiService.updateProfile(formData);
      if (response.success) {
        // Use the updateUser function from AuthContext to update the global user state
        updateUser(response.data.user);
        // Fallback to English if translation key doesn't exist yet
        toast.success(t('dashboard.profile.updateSuccess') || 'Profile updated successfully!');
        handleCloseModal();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      toast.error(err.message || t('dashboard.profile.updateError') || 'Failed to update profile.');
    }
  };

  if (isLoading) {
    return <div className="loading-spinner">{t('dashboard.profile.loading')}</div>;
  }

  if (!user) {
    return <div className="error-message">{t('dashboard.profile.loadFailed')}</div>;
  }

  const dateLocale = language === 'hi' ? 'hi-IN' : language === 'or' ? 'or-IN' : 'en-IN';

  return (
    <>
      <div className="profile-page-container">
        <h2>{t('dashboard.sidebar.myProfile')}</h2>
        <div className="profile-card">
          <div className="profile-avatar">
            <img src={user.avatar || `https://ui-avatars.com/api/?name=${user.fullName}&background=8B4513&color=fff`} alt={t('dashboard.profile.avatarAlt') || 'User Avatar'} />
          </div>
          <div className="profile-details">
            <div className="detail-item">
              <span className="detail-label">{t('dashboard.profile.fields.fullName')}</span>
              <span className="detail-value">{user.fullName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('dashboard.profile.fields.emailAddress')}</span>
              <span className="detail-value">{user.email}</span>
            </div>
            {/* Display phone number if it exists */}
            {user.phone && (
              <div className="detail-item">
                <span className="detail-label">{t('dashboard.profile.fields.phoneNumber') || 'Phone Number'}</span>
                <span className="detail-value">{user.phone}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">{t('dashboard.profile.fields.role')}</span>
              <span className="detail-value role-badge">{user.role}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('dashboard.profile.fields.memberSince')}</span>
              <span className="detail-value">{new Date(user.createdAt).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
          <div className="profile-actions">
            <button className="btn btn-primary" onClick={handleOpenModal}>{t('dashboard.profile.editProfile')}</button>
            <button className="btn btn-secondary" onClick={handleChangePassword}>{t('dashboard.profile.changePassword')}</button>
          </div>
        </div>
      </div>

      {/* --- RENDER THE MODAL --- */}
      <ProfileEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveProfile}
      />
    </>
  );
};

export default ProfilePage;